import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { 
  Database, 
  MessageCircle, 
  Users, 
  Heart, 
  Sparkles, 
  Zap,
  Shield,
  Lock,
  ArrowRight,
  CheckCircle2,
  Menu,
  X,
  Target,
  Eye,
  Award
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { features, howItWorksSteps, quizQuestions, quizResults, teamMembers } from '../mock';
import { useToast } from '../hooks/use-toast';

const iconMap = {
  'database': Database,
  'message-circle': MessageCircle,
  'users': Users,
  'heart': Heart,
  'sparkles': Sparkles,
  'zap': Zap
};

export const LandingPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [location, setLocation] = useState('');
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [quizComplete, setQuizComplete] = useState(false);
  const [quizScore, setQuizScore] = useState(0);
  const [selectedOption, setSelectedOption] = useState('');
  
  // Demo restriction dialog
  const [showDemoUsedDialog, setShowDemoUsedDialog] = useState(false);

  useEffect(() => {
    // Show quiz on first visit
    const hasSeenQuiz = localStorage.getItem('hasSeenQuiz');
    if (!hasSeenQuiz) {
      setTimeout(() => setShowQuiz(true), 1000);
    }
  }, []);

  const handleQuizAnswer = () => {
    if (!selectedOption) return;
    
    const question = quizQuestions[currentQuestion];
    const selectedAnswer = question.options.find(opt => opt.text === selectedOption);
    
    setAnswers({ ...answers, [currentQuestion]: selectedAnswer });
    setSelectedOption('');

    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Calculate score
      const totalScore = Object.values({ ...answers, [currentQuestion]: selectedAnswer })
        .reduce((sum, answer) => sum + answer.score, 0);
      setQuizScore(totalScore);
      setQuizComplete(true);
      localStorage.setItem('hasSeenQuiz', 'true');
    }
  };

  const getQuizResult = () => {
    if (quizScore <= quizResults.low.range[1]) return quizResults.low;
    if (quizScore <= quizResults.medium.range[1]) return quizResults.medium;
    return quizResults.high;
  };

  const closeQuiz = () => {
    setShowQuiz(false);
    setQuizComplete(false);
    setCurrentQuestion(0);
    setAnswers({});
    setQuizScore(0);
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    if (!acceptedPolicy) {
      toast({
        title: "Please accept the Privacy Policy",
        description: "You must agree to our Privacy Policy to join the waitlist.",
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${API_URL}/api/waitlist`, {
        name: name.trim(),
        email: email.trim(),
        age: age ? parseInt(age, 10) : null,
        location: location.trim() || null
      });

      setName('');
      setEmail('');
      setAge('');
      setLocation('');
      setAcceptedPolicy(false);
      setShowSuccess(true);
    } catch (error) {
      const message = error.response?.data?.detail || "Something went wrong. Please try again.";
      toast({
        title: "Could not join waitlist",
        description: message,
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleTryDemo = () => {
    const hasUsedDemo = localStorage.getItem('demoUsed');
    if (hasUsedDemo) {
      setShowDemoUsedDialog(true);
    } else {
      navigate('/demo');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Waitlist Success Dialog */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent className="sm:max-w-[480px] text-center">
          <DialogHeader>
            <div className="mx-auto w-16 h-16 bg-[#7E33E0]/10 rounded-full flex items-center justify-center mb-4">
              <CheckCircle2 className="h-9 w-9 text-[#7E33E0]" />
            </div>
            <DialogTitle className="text-2xl text-center">You're on the list! 🎉</DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              Thanks for joining the Connect Legacy waitlist. We'll be in touch soon with updates, early access, and exclusive beta invites.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-4">
            <Button
              onClick={() => setShowSuccess(false)}
              className="w-full bg-[#7E33E0] hover:bg-[#6B2BC0]"
              data-testid="waitlist-success-close"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Demo Already Used Dialog */}
      <Dialog open={showDemoUsedDialog} onOpenChange={setShowDemoUsedDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="text-2xl">You've Already Tried the Demo</DialogTitle>
            <DialogDescription>
              The interactive demo is designed for one-time use to give everyone a fresh experience.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-[#7E33E0]/10 border border-[#7E33E0]/20 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                Want to experience Connect Legacy fully? Join our waitlist to be the first to access the complete platform when we launch.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <Button 
                onClick={() => {
                  setShowDemoUsedDialog(false);
                  document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full bg-[#7E33E0] hover:bg-[#6B2BC0]"
              >
                Join Waitlist
              </Button>
              <Button 
                onClick={() => setShowDemoUsedDialog(false)}
                variant="outline"
                className="w-full"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quiz Dialog */}
      <Dialog open={showQuiz} onOpenChange={setShowQuiz}>
        <DialogContent className="sm:max-w-[600px]">
          {!quizComplete ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">How Prepared Is Your Digital Legacy?</DialogTitle>
                <DialogDescription>
                  Take this quick 5-question assessment to find out
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>Question {currentQuestion + 1} of {quizQuestions.length}</span>
                    <span>{Math.round(((currentQuestion) / quizQuestions.length) * 100)}%</span>
                  </div>
                  <Progress value={((currentQuestion) / quizQuestions.length) * 100} className="h-2" />
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">{quizQuestions[currentQuestion].question}</h3>
                  
                  <RadioGroup value={selectedOption} onValueChange={setSelectedOption}>
                    {quizQuestions[currentQuestion].options.map((option, idx) => (
                      <div key={idx} className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 hover:border-[#7E33E0] hover:bg-[#7E33E0]/5 transition-colors cursor-pointer">
                        <RadioGroupItem value={option.text} id={`option-${idx}`} />
                        <Label htmlFor={`option-${idx}`} className="flex-1 cursor-pointer">{option.text}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="ghost" onClick={closeQuiz}>Skip Quiz</Button>
                  <Button 
                    onClick={handleQuizAnswer}
                    disabled={!selectedOption}
                    className="bg-[#7E33E0] hover:bg-[#6B2BC0]"
                  >
                    {currentQuestion < quizQuestions.length - 1 ? 'Next Question' : 'See Results'}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl">{getQuizResult().title}</DialogTitle>
                <DialogDescription>Your Digital Legacy Score: {quizScore}/20</DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                <Progress value={(quizScore / 20) * 100} className="h-3" />
                
                <div className="space-y-4">
                  <p className="text-gray-700 leading-relaxed">{getQuizResult().description}</p>
                  
                  <div className="bg-[#7E33E0]/10 border border-[#7E33E0]/20 rounded-lg p-4">
                    <p className="font-medium text-[#7E33E0] mb-2">Our Recommendation:</p>
                    <p className="text-gray-700 text-sm">{getQuizResult().recommendation}</p>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button 
                    onClick={() => {
                      closeQuiz();
                      handleTryDemo();
                    }}
                    className="flex-1 bg-[#7E33E0] hover:bg-[#6B2BC0]"
                  >
                    Try Demo Now
                  </Button>
                  <Button 
                    onClick={closeQuiz}
                    variant="outline"
                    className="flex-1"
                  >
                    Explore Website
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex items-center space-x-2">
              <img src="/assets/cl-heart.png" alt="Connect Legacy" className="h-20 w-20 object-contain" />
              <span className="text-xl font-bold text-gray-900">Connect Legacy</span>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-[#7E33E0] transition-colors">Features</a>
              <a href="#how-it-works" className="text-gray-600 hover:text-[#7E33E0] transition-colors">How It Works</a>
              <a href="#contact" className="text-gray-600 hover:text-[#7E33E0] transition-colors">Contact</a>
              <Button onClick={handleTryDemo} variant="outline" className="border-[#7E33E0] text-[#7E33E0] hover:bg-[#7E33E0] hover:text-white">
                Try Demo
              </Button>
            </nav>

            {/* Mobile menu button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-[#7E33E0]"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden py-4 border-t border-gray-100">
              <div className="flex flex-col space-y-4">
                <a href="#features" className="text-gray-600 hover:text-[#7E33E0] transition-colors">Features</a>
                <a href="#how-it-works" className="text-gray-600 hover:text-[#7E33E0] transition-colors">How It Works</a>
                <a href="#contact" className="text-gray-600 hover:text-[#7E33E0] transition-colors">Contact</a>
                <Button onClick={handleTryDemo} variant="outline" className="border-[#7E33E0] text-[#7E33E0] hover:bg-[#7E33E0] hover:text-white w-full">
                  Try Demo
                </Button>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Abstract shapes */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#7E33E0]/5 rounded-full blur-3xl"></div>
          <div className="absolute top-40 right-10 w-96 h-96 bg-[#7E33E0]/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-[#7E33E0]/5 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-8">
            {/* Logo Banner */}
            <div className="flex justify-center mb-4">
              <img 
                src="/assets/cl-banner.png" 
                alt="Connect Legacy" 
                className="w-full max-w-2xl h-auto object-contain"
              />
            </div>

            <div className="inline-block">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#7E33E0]/10 text-[#7E33E0] text-sm font-medium">
                <Sparkles className="h-4 w-4 mr-2" />
                Secure Your Digital Future
              </span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
              Your Digital Life,
              <br />
              <span className="text-[#7E33E0]">Protected Forever</span>
            </h1>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Connect Legacy helps you organize and secure your digital assets, so your loved ones can access what matters most when it matters most.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Button 
                onClick={handleTryDemo}
                size="lg" 
                className="bg-[#7E33E0] hover:bg-[#6B2BC0] text-white px-8 py-6 text-lg"
              >
                Try Interactive Demo
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
                className="border-gray-300 text-gray-700 hover:border-[#7E33E0] hover:text-[#7E33E0] px-8 py-6 text-lg"
              >
                Join Waitlist
              </Button>
            </div>

            <div className="flex items-center justify-center gap-8 pt-8 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Lock className="h-4 w-4" />
                <span>Privacy Focused</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4" />
                <span>Secure Platform</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center space-y-6">
            <h2 className="text-4xl font-bold text-gray-900">
              What Happens to Your Digital Life?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Most people have years of precious photos, videos, voice notes, and messages scattered across their devices and cloud storage. 
              But when something happens, <span className="font-semibold text-gray-900">many families struggle</span> to access or even know these memories exist.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 pt-8">
              <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                    <X className="h-6 w-6 text-red-600" />
                  </div>
                  <CardTitle className="text-lg">Lost Access</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">Families can't access precious photos, videos, and personal messages</p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <X className="h-6 w-6 text-orange-600" />
                  </div>
                  <CardTitle className="text-lg">Unshared Stories</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">Life stories and meaningful moments never reach the people who'd treasure them</p>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <X className="h-6 w-6 text-purple-600" />
                  </div>
                  <CardTitle className="text-lg">Lost Memories</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">Precious photos, videos, and messages disappear forever</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-gray-900">Powerful Features for Peace of Mind</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to protect and manage your digital legacy in one secure platform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                  <CardHeader>
                    <div className="w-14 h-14 bg-[#7E33E0]/10 rounded-xl flex items-center justify-center mb-4">
                      <IconComponent className="h-7 w-7 text-[#7E33E0]" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 text-base">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-gray-900">How It Works</h2>
            <p className="text-lg text-gray-600">Get started in four simple steps</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorksSteps.map((step, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="w-16 h-16 bg-[#7E33E0] text-white rounded-full flex items-center justify-center text-2xl font-bold shadow-lg">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
                {index < howItWorksSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-gray-300 -translate-x-1/2"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#7E33E0]/10 via-white to-[#7E33E0]/5 -z-10"></div>
        
        <div className="container mx-auto max-w-4xl text-center space-y-8">
          <h2 className="text-4xl font-bold text-gray-900">
            Ready to Secure Your Digital Legacy?
          </h2>
          <p className="text-xl text-gray-600">
            Try our interactive demo and see how easy it is to protect what matters most.
          </p>
          <Button 
            onClick={handleTryDemo}
            size="lg" 
            className="bg-[#7E33E0] hover:bg-[#6B2BC0] text-white px-10 py-6 text-lg"
          >
            Launch Interactive Demo
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-gray-900">Our Mission</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Building a future where digital legacies are protected, accessible, and meaningful
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-14 h-14 bg-[#7E33E0]/10 rounded-xl flex items-center justify-center mb-4">
                  <Target className="h-7 w-7 text-[#7E33E0]" />
                </div>
                <CardTitle className="text-xl">Our Purpose</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  We believe everyone deserves a peaceful digital goodbye. Connect Legacy makes it simple to protect your digital life and the loved ones you leave behind.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-14 h-14 bg-[#7E33E0]/10 rounded-xl flex items-center justify-center mb-4">
                  <Eye className="h-7 w-7 text-[#7E33E0]" />
                </div>
                <CardTitle className="text-xl">Our Vision</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  A world where families never struggle with digital inheritance. Where precious memories, valuable assets, and important accounts are seamlessly passed on.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardHeader>
                <div className="w-14 h-14 bg-[#7E33E0]/10 rounded-xl flex items-center justify-center mb-4">
                  <Award className="h-7 w-7 text-[#7E33E0]" />
                </div>
                <CardTitle className="text-xl">Our Promise</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Strong security practices, intuitive design, and unwavering support. We're committed to making digital legacy planning accessible to everyone.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-gray-900">Meet Our Team</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              University friends united by a vision to transform digital legacy management
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {teamMembers.map((member, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                <CardHeader>
                  <div className="w-32 h-32 bg-gradient-to-br from-[#7E33E0]/20 to-[#7E33E0]/5 rounded-full mx-auto mb-4 flex items-center justify-center overflow-hidden">
                    {member.image ? (
                      <img 
                        src={member.image} 
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-4xl font-bold text-[#7E33E0]">{member.name.charAt(0)}</span>
                    )}
                  </div>
                  <CardTitle className="text-center text-xl">{member.name}</CardTitle>
                  <CardDescription className="text-center font-medium text-[#7E33E0]">
                    {member.role}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm leading-relaxed text-center">
                    {member.bio}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact/Waitlist Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="container mx-auto max-w-2xl text-center space-y-8">
          <h2 className="text-4xl font-bold">Join the Waitlist</h2>
          <p className="text-lg text-gray-300">
            Be the first to know when Connect Legacy launches. Get early access and exclusive benefits.
          </p>

          <form onSubmit={handleWaitlistSubmit} className="flex flex-col gap-4 max-w-md mx-auto">
            <Input
              type="text"
              placeholder="Your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              data-testid="waitlist-name-input"
              className="bg-white text-gray-900 border-none h-12"
            />
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="waitlist-email-input"
              className="bg-white text-gray-900 border-none h-12"
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                type="number"
                placeholder="Age"
                min="1"
                max="120"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                data-testid="waitlist-age-input"
                className="bg-white text-gray-900 border-none h-12"
              />
              <Input
                type="text"
                placeholder="Location (city/country)"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                data-testid="waitlist-location-input"
                className="bg-white text-gray-900 border-none h-12"
              />
            </div>

            <div className="flex items-start gap-2 text-left">
              <Checkbox
                id="accept-policy"
                checked={acceptedPolicy}
                onCheckedChange={setAcceptedPolicy}
                data-testid="waitlist-policy-checkbox"
                className="mt-0.5 border-gray-400 data-[state=checked]:bg-[#7E33E0] data-[state=checked]:border-[#7E33E0]"
              />
              <Label htmlFor="accept-policy" className="text-sm text-gray-300 leading-relaxed cursor-pointer">
                By signing up, you agree to our{' '}
                <Link
                  to="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#7E33E0] hover:text-[#9B5BE8] underline"
                  data-testid="waitlist-policy-link"
                >
                  Privacy Policy
                </Link>
                .
              </Label>
            </div>

            <Button 
              type="submit"
              size="lg"
              disabled={submitting}
              data-testid="waitlist-submit-btn"
              className="bg-[#7E33E0] hover:bg-[#6B2BC0] text-white px-8 disabled:opacity-50"
            >
              {submitting ? 'Joining...' : 'Join Waitlist'}
            </Button>
          </form>

          <div className="pt-4">
            <p className="text-gray-400 text-sm">Have questions? Reach out to us at</p>
            <a 
              href="mailto:support@connect-legacy.com" 
              className="text-[#7E33E0] hover:text-[#9B5BE8] font-medium text-lg transition-colors"
            >
              support@connect-legacy.com
            </a>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6 pt-8 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#7E33E0]" />
              <span>No spam, ever</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#7E33E0]" />
              <span>Exclusive early access</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#7E33E0]" />
              <span>Priority support</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 text-gray-400 py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <img src="/assets/cl-heart.png" alt="Connect Legacy" className="h-20 w-20 object-contain" />
                <span className="text-lg font-bold text-white">Connect Legacy</span>
              </div>
              <p className="text-sm mb-4">
                Protecting your digital life for the people who matter most.
              </p>
              <a 
                href="mailto:support@connect-legacy.com" 
                className="text-sm text-[#7E33E0] hover:text-[#9B5BE8] transition-colors block mb-2"
              >
                support@connect-legacy.com
              </a>
              <p className="text-xs text-gray-500">
                © 2026 Connect Legacy. All rights reserved.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-[#7E33E0] transition-colors">Features</a></li>
                <li><a href="#how-it-works" className="hover:text-[#7E33E0] transition-colors">How It Works</a></li>
                <li><a href="#" className="hover:text-[#7E33E0] transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-[#7E33E0] transition-colors">Security</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-[#7E33E0] transition-colors">About</a></li>
                <li><a href="#contact" className="hover:text-[#7E33E0] transition-colors">Contact</a></li>
                <li><Link to="/privacy-policy" className="hover:text-[#7E33E0] transition-colors">Privacy</Link></li>
                <li><Link to="/terms-of-use" className="hover:text-[#7E33E0] transition-colors">Terms of Use</Link></li>
                <li><Link to="/cookie-policy" className="hover:text-[#7E33E0] transition-colors">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};